import { useMemo } from 'react';

import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useFormikContext } from 'formik';

import { CondicaoPagamentoAutoComplete } from 'components/autocompletes/CondicaoPagamentoAutoComplete';
import { CissNumberField } from 'components/fields';
import { PanelGrid } from 'components/grid';
import { ConfigPedidoRemoveBtn } from 'pages/compra/configuracao-pedido/components';
import { useConfigPedidoListContext } from 'pages/compra/configuracao-pedido/contexts';
import { ConfigPedidoQuebrasDinamicas, ProdutoQuebra } from 'pages/compra/configuracao-pedido/types';
import { formatDateHour } from 'util/format';

type ConfigPedidoQuebrasDinamicasGrid = {
    handleExcluir: (idList: Set<GridRowId>) => void;
};

/**
 * Transforma os produtos em linhas para o grid.
 */
const getRows = (products: ProdutoQuebra[] = []) =>
    products.map((item, index) => ({
        id: `${item.produto?.idProduto}_${item.nrQuebra}`,
        index,
        idProduto: item.produto?.idProduto,
        cdProduto: item.produto?.cdProduto || '',
        dsProduto: item.produto?.dsProduto,
        qtQuebra: item.qtQuebra,
        nrQuebra: item.nrQuebra,
        peQuebra: item.peQuebra,
        dtInicio: item.dtInicio && formatDateHour(item.dtInicio),
        dtFim: item.dtFim && formatDateHour(item.dtFim),
        dsCondicaoPagamento: item.condicaoPagamento?.dsCondicaoPagamento,
        idCondicaoPagamento: item.condicaoPagamento?.idCondicaoPagamento,
        fgAtivo: item.fgAtivo ?? true,
        condicaoPagamento: item.condicaoPagamento,
    }));

type GridRows = ReturnType<typeof getRows>;

/**
 * Filtra os produtos com base no valor do filtro.
 */
const getFilteredRows = (products: GridRows, valueFilter: string) =>
    products.filter(
        (item) =>
            !valueFilter ||
            item.dsProduto?.toLowerCase().includes(valueFilter.trim().toLowerCase()) ||
            item.cdProduto?.toString().includes(valueFilter.trim().toLowerCase()),
    );

export function ConfigPedidoQuebrasDinamicasGrid({ handleExcluir }: ConfigPedidoQuebrasDinamicasGrid) {
    const { valueFilter, setSelectedRows } = useConfigPedidoListContext();
    const { values, setFieldValue } = useFormikContext<ConfigPedidoQuebrasDinamicas>();

    const rows = useMemo(() => {
        const rows = getRows(values.configPedidoQuebrasDinamicas);

        return getFilteredRows(rows, valueFilter);
    }, [values.configPedidoQuebrasDinamicas, valueFilter]);

    const columns = useMemo(
        (): GridColDef[] => [
            { field: 'cdProduto', headerName: 'Código do Produto', minWidth: 120 },
            { field: 'dsProduto', headerName: 'Nome do Produto', width: 150, minWidth: 120 },
            { field: 'qtQuebra', headerName: 'Total de Quebras', minWidth: 80 },
            { field: 'nrQuebra', headerName: 'N.º da Quebra', minWidth: 80 },
            {
                field: 'peQuebra',
                headerName: '%',
                minWidth: 80,
                headerAlign: 'center',
                align: 'center',
                renderCell: ({ row }) => (
                    <CissNumberField
                        size="small"
                        maxValue={100}
                        forceMaxValue
                        allowNegative={false}
                        value={row.peQuebra}
                        onChange={(e) => setFieldValue(`configPedidoQuebrasDinamicas.${row.index}.peQuebra`, +e.target.value)}
                    />
                ),
            },
            {
                field: 'dtInicio',
                headerName: 'Início',
                width: 160,
                minWidth: 140,
                renderCell: ({ row }) => (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <DatePicker
                            format="dd/MM/yyyy"
                            value={row.dtInicio ? parseISO(row.dtInicio) : null}
                            onChange={(value) =>
                                setFieldValue(`configPedidoQuebrasDinamicas.${row.index}.dtInicio`, value ? format(value, 'yyyy-MM-dd') : '')
                            }
                            slotProps={{ textField: { size: 'small' } }}
                        />
                    </LocalizationProvider>
                ),
            },
            {
                field: 'dtFim',
                headerName: 'Fim',
                width: 160,
                minWidth: 140,
                renderCell: ({ row }) => (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <DatePicker
                            format="dd/MM/yyyy"
                            value={row.dtFim ? parseISO(row.dtFim) : null}
                            onChange={(value) =>
                                setFieldValue(`configPedidoQuebrasDinamicas.${row.index}.dtFim`, value ? format(value, 'yyyy-MM-dd') : '')
                            }
                            slotProps={{ textField: { size: 'small' } }}
                        />
                    </LocalizationProvider>
                ),
            },
            {
                field: 'dsCondicaoPagamento',
                headerName: 'Condição de Pagamento',
                minWidth: 160,
                flex: 1,
                renderCell: ({ row }) => (
                    <CondicaoPagamentoAutoComplete
                        label=""
                        size="small"
                        sx={{ width: '100%' }}
                        value={row.condicaoPagamento}
                        onChange={(e, value) => setFieldValue(`configPedidoQuebrasDinamicas.${row.index}.condicaoPagamento`, value)}
                    />
                ),
            },
            {
                field: 'acoes',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Ações',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                minWidth: 80,
                renderCell: ({ row }) => <ConfigPedidoRemoveBtn onClick={() => handleExcluir(new Set([`${row.idProduto}_${row.nrQuebra}`]))} />,
            },
        ],
        [values.configPedidoQuebrasDinamicas],
    );

    return <PanelGrid rows={rows} columns={columns} loading={false} reload={false} remote={false} checkboxSelection onClickRow={setSelectedRows} />;
}
